// Contrats du plan précis des marchés à ciel ouvert (cadrage plan-precis-marches.md, D-300 à D-309) :
// îlots découpés en places, ajustements manuels des places, relevé d'îlot et recensement sur le
// terrain (charges de la file de synchronisation), imports d'un plan et d'une liste de commerçants.
import { z } from 'zod';
import {
  schemaCodeRubrique,
  schemaPosition,
  schemaReferencePlan,
  RUBRIQUE_EMPLACEMENT_DEFAUT,
} from './marches-structure';

const coord = z.tuple([z.number().min(-180).max(180), z.number().min(-85).max(85)]);
const uuid = z.string().uuid('Identifiant invalide');
const version = z.number().int().nonnegative();
const jour = z.string().regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/, 'Date AAAA-MM-JJ attendue');
const metres = (max: number) => z.number().positive().max(max);

export const schemaCodeIlot = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9][A-Za-z0-9]{0,9}$/, 'Code d’îlot de 1 à 10 lettres ou chiffres');

export const schemaNumerotation = z.strictObject({
  format: z.enum(['ilot_rangee_rang', 'ilot_continu']).default('ilot_rangee_rang'),
  sens: z.enum(['meme_sens', 'alterne']).default('meme_sens'),
  premier_numero: z.number().int().min(0).max(9999).default(1),
  chiffres: z.number().int().min(1).max(5).default(3),
});
export type Numerotation = z.infer<typeof schemaNumerotation>;

/** Plafonds d'un îlot : 200 rangées et 500 places par rangée, 5 000 places au plus par îlot. */
export const MAX_PLACES_ILOT = 5000;

export const schemaCreationIlot = z
  .strictObject({
    code: schemaCodeIlot.optional(),
    nom: z.string().trim().min(1).max(120),
    coins: z.array(coord).length(4),
    rangees: z.number().int().min(1).max(200),
    places_par_rangee: z.number().int().min(1).max(500),
    largeur_type_m: metres(50).default(2),
    profondeur_type_m: metres(50).default(2),
    numerotation: schemaNumerotation.default({
      format: 'ilot_rangee_rang',
      sens: 'meme_sens',
      premier_numero: 1,
      chiffres: 3,
    }),
    rubrique_code: schemaCodeRubrique.default(RUBRIQUE_EMPLACEMENT_DEFAUT),
    zone_id: uuid.optional(),
  })
  .refine((v) => v.rangees * v.places_par_rangee <= MAX_PLACES_ILOT, {
    path: ['places_par_rangee'],
    message: `Un îlot compte au plus ${MAX_PLACES_ILOT} places`,
  });
export type CreationIlot = z.infer<typeof schemaCreationIlot>;

/** Recalage au bureau (nouveau contour ou nouveau comptage) : redécoupe sans changer la précision. */
export const schemaRecalageIlot = z.strictObject({
  coins: z.array(coord).length(4),
  rangees: z.number().int().min(1).max(200),
  places_par_rangee: z.number().int().min(1).max(500),
  facade_m: metres(2000).optional(),
  version_attendue: version,
});

export const schemaRenumerotationIlot = z.strictObject({
  numerotation: schemaNumerotation,
  motif: z.string().trim().min(3).max(300),
  version_attendue: version,
});

// Ajustements manuels d'une place ----------------------------------------------------------------

/** Un objet unique (validable comme DTO) : tourner (angle_deg), redimensionner (largeur_m, profondeur_m) ou déplacer (position). */
export const schemaAjustementPlace = z
  .strictObject({
    action: z.enum(['tourner', 'redimensionner', 'deplacer']),
    angle_deg: z.number().min(-360).max(360).optional(),
    largeur_m: metres(50).optional(),
    profondeur_m: metres(50).optional(),
    position: schemaPosition.optional(),
    version_attendue: version,
  })
  .refine(
    (v) =>
      v.action === 'tourner'
        ? v.angle_deg !== undefined
        : v.action === 'redimensionner'
          ? v.largeur_m !== undefined && v.profondeur_m !== undefined
          : v.position !== undefined,
    {
      message: 'Valeur de l’ajustement manquante (angle, dimensions ou position)',
      path: ['action'],
    },
  );
export type AjustementPlace = z.infer<typeof schemaAjustementPlace>;

export const schemaScissionPlace = z.strictObject({
  parts: z.number().int().min(2).max(4),
  version_attendue: version,
});
export const schemaFusionPlaces = z.strictObject({
  places: z
    .array(z.strictObject({ id: uuid, version_attendue: version }))
    .min(2)
    .max(6),
});
export const schemaRetraitPlace = z.strictObject({
  motif: z.string().trim().min(3).max(300),
  version_attendue: version,
});
export const schemaRenumerotationPlace = z.strictObject({
  reference: schemaReferencePlan,
  motif: z.string().trim().min(3).max(300),
  version_attendue: version,
});
export const schemaAjoutPlace = z.strictObject({
  position: schemaPosition,
  reference: schemaReferencePlan,
  largeur_m: metres(50).default(2),
  profondeur_m: metres(50).default(2),
  orientation_deg: z.number().min(0).max(360).optional(),
  rangee: z.number().int().min(1).max(200).optional(),
});

// Terrain : relevé d'îlot et recensement --------------------------------------------------------

/** Coin relevé : position moyennée sur l'appareil (30 s visées), nombre d'échantillons et écart. */
export const schemaCoinReleve = z.strictObject({
  position: schemaPosition,
  echantillons: z.number().int().min(1).max(1000),
  duree_s: z.number().min(0).max(600),
  ecart_m: z.number().min(0).max(1000).optional(),
});

export const schemaChargeReleveIlot = z.strictObject({
  coins: z.array(schemaCoinReleve).length(4),
  rangees: z.number().int().min(1).max(200),
  places_par_rangee: z.number().int().min(1).max(500),
  facade: z
    .strictObject({
      methode: z.enum(['metre', 'pas']),
      valeur: z.number().positive().max(5000),
      longueur_pas_m: z.number().min(0.3).max(1.2).optional(),
    })
    .optional(),
});
export type ChargeReleveIlot = z.infer<typeof schemaChargeReleveIlot>;

/** Longueur de pas retenue par défaut quand l'agent compte au pas (D-303). */
export const LONGUEUR_PAS_DEFAUT_M = 0.75;

export const schemaChargeRecensementPlace = z
  .strictObject({
    statut: z.enum(['occupee', 'libre']),
    occupant: z
      .strictObject({
        nom: z.string().trim().min(2).max(200),
        telephone: z
          .string()
          .trim()
          .regex(/^\+?[0-9][0-9 .-]{5,19}$/, 'Numéro de téléphone invalide')
          .optional(),
        activite: z.string().trim().min(2).max(120).optional(),
      })
      .optional(),
    photo_id_client: uuid.optional(),
    confirme: z.literal(true),
  })
  .refine((v) => (v.statut === 'occupee') === Boolean(v.occupant), {
    path: ['occupant'],
    message: 'L’occupant est saisi pour une place occupée, et seulement pour elle',
  });
export type ChargeRecensementPlace = z.infer<typeof schemaChargeRecensementPlace>;

export const ETATS_RECENSEMENT = ['retenu', 'confirme', 'doublon_signale', 'ecarte'] as const;
export const LIBELLES_ETAT_RECENSEMENT: Record<(typeof ETATS_RECENSEMENT)[number], string> = {
  retenu: 'Retenu',
  confirme: 'Confirmation',
  doublon_signale: 'Doublon signalé',
  ecarte: 'Écarté',
};

export const schemaDecisionRecensement = z.strictObject({
  decision: z.enum(['retenir', 'ecarter', 'distinct', 'meme_personne']),
  motif: z.string().trim().min(3).max(300),
});

export const schemaAttributionRecensement = z.strictObject({
  periode: z
    .strictObject({ debut: jour, fin: jour })
    .refine((p) => p.fin >= p.debut, { message: 'La fin suit le début', path: ['fin'] }),
  decision_reference: z.string().trim().max(120).optional(),
});

// Imports ------------------------------------------------------------------------------------------

export const schemaImportPlan = z.strictObject({
  format: z.enum(['geojson', 'kml']),
  contenu: z.string().min(1).max(5_000_000),
  appliquer: z.boolean().default(false),
  rubrique_code: schemaCodeRubrique.default(RUBRIQUE_EMPLACEMENT_DEFAUT),
});

export const schemaImportCommercants = z.strictObject({
  contenu: z.string().min(1).max(2_000_000),
  appliquer: z.boolean().default(false),
});

export interface LigneRapportImport {
  numero: number;
  libelle: string;
  statut: 'ok' | 'erreur' | 'signal';
  message: string;
}
export interface RapportImport {
  valide: boolean;
  applique: boolean;
  lignes: LigneRapportImport[];
  resume: Record<string, number>;
}

export const coordonneeIlot = coord;
